import { useMemo, useState, useEffect, useRef } from 'react';
import { Icon } from './Icon';
import { useAuth } from '../../contexts/AuthContext';
import { useRecipes, useSwipes, useAllFeedback, useFamilyMembers, useHouseholdSettings } from '../../hooks/useSupabase';
import { useWeeklyBoard } from '../../hooks/useWeeklyBoard';
import { generateWeightedPlan } from '../../lib/planGenerator';
import { dinnerEventsToBoard } from '../../lib/planLifecycle';
import { buildShoppingListFromEvents, SECTION_ORDER, SECTION_LABELS } from '../../lib/shoppingList';
import { trackEvent } from '../../lib/analytics';
import { buildPrepChecklist } from '../../lib/prepChecklist';

// ────────────────────────────────────────────────────────────────────────────
// Weekly planner board — the seven-column plan canvas (draft -> review -> lock),
// wired to the normalized dinner_events model (MEA-179 / MEA-182). "Draft this
// week" runs the generator and persists dinner_events; "Lock the week" calls the
// planner-gated RPC. Every value reads a --dl-* token the app already defines.
// ────────────────────────────────────────────────────────────────────────────

const SOURCES = {
  home: { label: 'Home cooked', icon: 'chef-hat', bg: 'var(--dl-source-cooked-bg)', fg: 'var(--dl-source-cooked-text)', border: 'var(--dl-source-border)' },
  leftovers: { label: 'Leftovers', icon: 'package', bg: 'var(--dl-source-leftover-bg)', fg: 'var(--dl-source-leftover-text)', border: 'var(--dl-source-border)' },
  frozen: { label: 'Frozen', icon: 'snowflake', bg: 'var(--dl-source-frozen-bg)', fg: 'var(--dl-source-frozen-text)', border: 'var(--dl-source-border)' },
  takeout: { label: 'Takeout', icon: 'shopping-bag', bg: 'var(--dl-source-takeout-bg)', fg: 'var(--dl-source-takeout-text)', border: 'var(--dl-source-easy-border)' },
};

const STATUS = {
  draft: { label: 'Draft', icon: 'notebook-pen', bg: 'var(--dl-status-draft-bg)', border: 'var(--dl-status-draft-border)', fg: 'var(--dl-status-draft-text)' },
  in_review: { label: 'In review', icon: 'users', bg: 'var(--dl-status-review-bg)', border: 'var(--dl-status-review-border)', fg: 'var(--dl-status-review-text)' },
  locked: { label: 'Locked', icon: 'lock', bg: 'var(--dl-status-locked-bg)', border: 'var(--dl-status-locked-border)', fg: 'var(--dl-status-locked-text)' },
};

const CARD = {
  locked: { background: 'var(--dl-status-locked-bg)', border: '1px solid var(--dl-status-locked-border)' },
  in_review: { background: 'var(--dl-surface)', border: '1px solid var(--dl-status-review-border)' },
  draft: { background: 'var(--dl-status-draft-bg)', border: '1px solid var(--dl-status-draft-border)' },
};

function Button({ variant = 'primary', icon, size = 'md', children, onClick, disabled }) {
  const tones = {
    primary: { bg: 'var(--dl-primary)', fg: 'var(--dl-primary-fg)', border: 'var(--dl-primary)' },
    secondary: { bg: 'var(--dl-surface)', fg: 'var(--dl-text)', border: 'var(--dl-border)' },
    ai: { bg: 'var(--dl-ai)', fg: 'var(--dl-ai-fg)', border: 'var(--dl-ai)' },
  };
  const t = tones[variant] || tones.primary;
  const sm = size === 'sm';
  return (
    <button
      type="button" onClick={onClick} disabled={disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: sm ? 6 : 8,
        height: sm ? 'var(--dl-control-height-sm, 30px)' : 'var(--dl-control-height-md, 36px)',
        padding: sm ? '0 10px' : '0 14px', background: t.bg, color: t.fg,
        border: '1px solid ' + t.border, borderRadius: 'var(--dl-radius-md, 8px)',
        font: 'var(--dl-weight-semibold) ' + (sm ? 'var(--dl-size-label)' : 'var(--dl-size-body-sm)') + '/1 var(--dl-font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', opacity: disabled ? 0.5 : 1,
        transition: 'var(--dl-transition-control, all 120ms ease)',
      }}
    >
      {icon && <Icon name={icon} size={sm ? 14 : 16} strokeWidth={2.25} />}
      {children}
    </button>
  );
}

function StatusChip({ status = 'draft', size = 'md' }) {
  const s = STATUS[status] || STATUS.draft;
  const small = size === 'sm';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 'var(--dl-space-3, 4px)',
      height: small ? 20 : 24, padding: small ? '0 7px' : '0 9px', borderRadius: 'var(--dl-radius-pill, 999px)',
      background: s.bg, border: '1px solid ' + s.border, color: s.fg,
      font: 'var(--dl-weight-semibold) ' + (small ? 'var(--dl-size-micro)' : 'var(--dl-size-meta)') + '/1 var(--dl-font-sans)',
      letterSpacing: 'var(--dl-tracking-label, 0.01em)', whiteSpace: 'nowrap',
    }}>
      <Icon name={s.icon} size={small ? 11 : 12} strokeWidth={2.25} />
      {s.label}
    </span>
  );
}

function SourceChip({ source = 'home' }) {
  const s = SOURCES[source] || SOURCES.home;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 'var(--dl-space-3, 4px)',
      height: 22, padding: '0 7px', borderRadius: 'var(--dl-radius-pill, 999px)',
      background: s.bg, border: '1px solid ' + s.border, color: s.fg,
      font: 'var(--dl-weight-medium) var(--dl-size-micro)/1 var(--dl-font-sans)', whiteSpace: 'nowrap',
    }}>
      <Icon name={s.icon} size={12} strokeWidth={2} />
      {s.label}
    </span>
  );
}

function Badge({ icon, children }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, height: 20, padding: '0 7px',
      borderRadius: 'var(--dl-radius-pill, 999px)', background: 'var(--dl-caution-bg)',
      border: '1px solid var(--dl-caution-border)', color: 'var(--dl-caution-text)',
      font: 'var(--dl-weight-semibold) var(--dl-size-micro)/1 var(--dl-font-sans)', whiteSpace: 'nowrap',
    }}>
      {icon && <Icon name={icon} size={11} strokeWidth={2.25} />}
      {children}
    </span>
  );
}

function MealCard({ title, status = 'draft', source }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 12, ...(CARD[status] || CARD.draft) }}>
      <span style={{ font: 'var(--dl-type-meal-title)', letterSpacing: '-0.01em' }}>{title}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {source && <SourceChip source={source} />}
        {status === 'in_review' && <StatusChip status="in_review" size="sm" />}
      </div>
    </div>
  );
}

function EmptyNightSlot() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 'var(--dl-space-3, 4px)',
      width: '100%', minHeight: 'var(--dl-meal-card-min-height, 98px)', padding: 'var(--dl-space-5, 12px)',
      border: 'var(--dl-stroke-dash, 1.5px) dashed var(--dl-status-unplanned-border)', borderRadius: 'var(--dl-radius-lg, 12px)',
      color: 'var(--dl-status-unplanned-text)', font: 'var(--dl-type-label)',
    }}>
      <Icon name="plus" size={16} />
      Open night
    </div>
  );
}

function DayHeader({ day, date, variant, badge }) {
  const busy = variant === 'busy';
  const open = variant === 'open';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', borderRadius: 8,
      background: busy ? 'var(--dl-night-busy-bg)' : 'transparent',
      border: '1px solid ' + (busy ? 'var(--dl-night-busy-border)' : 'transparent'),
    }}>
      {variant === 'today' && <span style={{ width: 6, height: 6, borderRadius: 999, background: 'var(--dl-day-today-marker)', flex: '0 0 auto' }} />}
      <span style={{
        font: 'var(--dl-weight-semibold) 11px/1 var(--dl-font-sans)', letterSpacing: '0.08em', textTransform: 'uppercase',
        color: busy ? 'var(--dl-night-busy-text)' : open ? 'var(--dl-night-open-text)' : 'var(--dl-text)',
      }}>{day}</span>
      {date != null && <span style={{ font: '400 11px/1 var(--dl-font-mono)', color: 'var(--dl-text-muted)' }}>{dayOfMonth(date)}</span>}
      {badge && <><span style={{ flex: 1 }} />{badge}</>}
    </div>
  );
}

function dayOfMonth(iso) {
  return iso ? Number(iso.split('-')[2]) : '';
}

function formatWeekOf(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

function todayISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}

const SOURCE_OPTIONS = [
  { value: 'catalog', label: 'Home cooked' },
  { value: 'leftover', label: 'Leftovers' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'takeout', label: 'Takeout' },
];

// Edit a single night (MEA-205): swap the meal, type a takeout/leftover title,
// set the source, or tag it busy. "Clear night" drops it back to an open night.
function NightEditor({ date, event, recipes, busy, onSave, onClear, onClose }) {
  const [recipeId, setRecipeId] = useState(event?.recipe_id || '');
  const [title, setTitle] = useState(event?.title || '');
  const [source, setSource] = useState(event?.source || 'catalog');
  const [isBusy, setIsBusy] = useState(event?.is_busy || false);
  const [note, setNote] = useState(event?.note || '');
  const canSave = Boolean(recipeId || title.trim());

  const input = {
    width: '100%', padding: '8px 10px', borderRadius: 'var(--dl-radius-md, 8px)',
    border: '1px solid var(--dl-border)', background: 'var(--dl-surface)', color: 'var(--dl-text)',
    font: 'var(--dl-type-body-sm)',
  };
  const label = { display: 'block', marginBottom: 4, font: 'var(--dl-type-label)', color: 'var(--dl-text-secondary)' };

  const save = () => onSave({
    recipe_id: recipeId || null,
    title: recipeId ? null : (title.trim() || null),
    source, is_busy: isBusy, note: note.trim() || null, status: 'draft',
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(20,23,27,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 420, background: 'var(--dl-surface)', border: '1px solid var(--dl-border)', borderRadius: 'var(--dl-radius-lg, 12px)', padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <span style={{ font: 'var(--dl-weight-semibold) var(--dl-size-heading)/1.25 var(--dl-font-sans)' }}>Edit {formatWeekOf(date)}</span>
        <div>
          <label style={label}>Meal</label>
          <select value={recipeId} onChange={(e) => setRecipeId(e.target.value)} style={input}>
            <option value="">— type your own below —</option>
            {(recipes || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        {!recipeId && (
          <div>
            <label style={label}>Or type it (takeout, leftovers…)</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Thai from Bahn" style={input} />
          </div>
        )}
        <div>
          <label style={label}>Source</label>
          <select value={source} onChange={(e) => setSource(e.target.value)} style={input}>
            {SOURCE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, font: 'var(--dl-type-body-sm)', color: 'var(--dl-text)' }}>
          <input type="checkbox" checked={isBusy} onChange={(e) => setIsBusy(e.target.checked)} />
          Busy night
        </label>
        {isBusy && <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Soccer until 7" style={input} />}
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <Button variant="primary" onClick={save} disabled={busy || !canSave}>Save</Button>
          {event && <Button variant="secondary" onClick={onClear} disabled={busy}>Clear night</Button>}
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        </div>
      </div>
    </div>
  );
}

// Grocery list from the locked week's dinner_events (MEA-190), grouped by aisle
// with ephemeral check-off. Generation lives in lib/shoppingList.
function ShoppingListPanel({ items, checked, onToggle }) {
  const groups = SECTION_ORDER
    .map((section) => ({ section, label: SECTION_LABELS[section], rows: items.filter((i) => i.section === section) }))
    .filter((g) => g.rows.length);
  const remaining = items.length - checked.size;
  return (
    <div style={{ maxWidth: 640 }}>
      <p style={{ font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-secondary)', margin: '0 0 16px' }}>
        From your locked week · {remaining} to buy
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {groups.map((g) => (
          <div key={g.section} style={{ background: 'var(--dl-surface)', border: '1px solid var(--dl-border)', borderRadius: 'var(--dl-radius-lg, 12px)', padding: '12px 16px' }}>
            <div style={{ font: 'var(--dl-weight-semibold) var(--dl-size-label)/1.35 var(--dl-font-sans)', color: 'var(--dl-text-secondary)', marginBottom: 6 }}>{g.label}</div>
            {g.rows.map((it) => {
              const key = `${it.section}:${it.item}`;
              const isChecked = checked.has(key);
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', cursor: 'pointer', font: 'var(--dl-type-body-sm)', color: isChecked ? 'var(--dl-text-muted)' : 'var(--dl-text)', textDecoration: isChecked ? 'line-through' : 'none' }}>
                  <input type="checkbox" checked={isChecked} onChange={() => onToggle(key)} />
                  <span>{it.amount ? `${it.amount}${it.unit ? ' ' + it.unit : ''} ` : ''}{it.item}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick} style={{
      padding: '6px 12px', border: 'none', borderRadius: 'var(--dl-radius-md, 8px)', cursor: 'pointer',
      background: active ? 'var(--dl-surface)' : 'transparent',
      color: active ? 'var(--dl-text)' : 'var(--dl-text-secondary)',
      font: 'var(--dl-weight-semibold) var(--dl-size-label)/1 var(--dl-font-sans)',
      boxShadow: active ? 'var(--dl-elevation-1, 0 1px 2px rgba(27,31,36,0.08))' : 'none',
    }}>{children}</button>
  );
}

// Prep tasks by day from the locked week (MEA-191): each night's recipe steps as
// a checklist. Takeout/leftover/open nights are skipped upstream.
function PrepPanel({ days, checked, onToggle }) {
  const totalSteps = days.reduce((n, d) => n + d.steps.length, 0);
  return (
    <div style={{ maxWidth: 640 }}>
      <p style={{ font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-secondary)', margin: '0 0 16px' }}>
        {totalSteps > 0 ? `${checked.size} of ${totalSteps} done` : 'No prep needed this week'}
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {days.map((d) => (
          <div key={d.date} style={{ background: 'var(--dl-surface)', border: '1px solid var(--dl-border)', borderRadius: 'var(--dl-radius-lg, 12px)', padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ font: 'var(--dl-weight-semibold) 11px/1 var(--dl-font-sans)', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--dl-text-secondary)' }}>{d.day}</span>
              <span style={{ font: 'var(--dl-type-meal-title)' }}>{d.name}</span>
              {(d.prepTime > 0 || d.cookTime > 0) && (
                <span style={{ marginLeft: 'auto', font: 'var(--dl-type-meta)', color: 'var(--dl-text-muted)' }}>
                  {d.prepTime > 0 ? `${d.prepTime}m prep` : ''}{d.prepTime > 0 && d.cookTime > 0 ? ' · ' : ''}{d.cookTime > 0 ? `${d.cookTime}m cook` : ''}
                </span>
              )}
            </div>
            {d.steps.length === 0 ? (
              <p style={{ font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-muted)', margin: 0 }}>No prep steps.</p>
            ) : d.steps.map((step, i) => {
              const key = `${d.date}:${i}`;
              const isChecked = checked.has(key);
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0', cursor: 'pointer', font: 'var(--dl-type-body-sm)', color: isChecked ? 'var(--dl-text-muted)' : 'var(--dl-text)', textDecoration: isChecked ? 'line-through' : 'none' }}>
                  <input type="checkbox" checked={isChecked} onChange={() => onToggle(key)} style={{ marginTop: 3 }} />
                  <span>{step.text}{step.time ? ` (${step.time})` : ''}</span>
                </label>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function WeeklyPlanner() {
  const { householdId } = useAuth();
  const { recipes } = useRecipes(householdId);
  const { swipes } = useSwipes(householdId);
  const { feedback } = useAllFeedback(householdId);
  const { members } = useFamilyMembers(householdId);
  const { plannedMeals } = useHouseholdSettings(householdId);
  const { weekOf, events, status, loading, busy, error, persistDraft, lockWeek, revertWeek, updateEvent, clearEvent, fillNight } = useWeeklyBoard(householdId);
  const [editing, setEditing] = useState(null); // { date, event | null }
  const [notice, setNotice] = useState(null); // gentle inline reminder (e.g. redraft-while-locked)

  const recipeMap = useMemo(() => Object.fromEntries((recipes || []).map((r) => [r.id, r])), [recipes]);
  const cols = useMemo(() => dinnerEventsToBoard(events, { weekOf, recipeMap }), [events, weekOf, recipeMap]);
  const today = todayISO();
  const plannedCount = events.filter((e) => e.status && e.status !== 'unplanned').length;
  const hasPlan = events.length > 0;
  const locked = status === 'locked';
  const eventByDate = useMemo(() => Object.fromEntries(events.map((e) => [e.event_date, e])), [events]);
  const groceries = useMemo(() => (locked ? buildShoppingListFromEvents(events, recipeMap) : []), [locked, events, recipeMap]);
  const [checkedItems, setCheckedItems] = useState(() => new Set());
  const toggleItem = (key) => setCheckedItems((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Fire grocery_list_opened once per lock (reset on unlock).
  const groceryLogged = useRef(false);
  useEffect(() => {
    if (locked && !groceryLogged.current) { groceryLogged.current = true; trackEvent('grocery_list_opened', { week_of: weekOf }); }
    if (!locked) groceryLogged.current = false;
  }, [locked, weekOf]);

  const prep = useMemo(() => (locked ? buildPrepChecklist(events, recipeMap, { weekOf }) : []), [locked, events, recipeMap, weekOf]);
  const [prepChecked, setPrepChecked] = useState(() => new Set());
  const togglePrep = (key) => setPrepChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) { next.delete(key); } else { next.add(key); trackEvent('prep_task_completed', { week_of: weekOf }); }
    return next;
  });
  const [lockedTab, setLockedTab] = useState('shopping');

  const onDraft = () => {
    if (locked) {
      // Redrafting would overwrite the locked plan — nudge to unlock first.
      setNotice('This week is locked. Unlock to edit before redrafting.');
      return;
    }
    setNotice(null);
    const plan = generateWeightedPlan(recipes, swipes, feedback, members, plannedMeals);
    persistDraft(plan);
  };
  // Clear the reminder once the week is unlocked.
  useEffect(() => { if (!locked) setNotice(null); }, [locked]);

  const openEditor = (date) => { if (!locked) setEditing({ date, event: eventByDate[date] || null }); };
  const handleSave = (patch) => {
    if (editing.event) updateEvent(editing.event.id, patch);
    else fillNight(editing.date, patch);
    setEditing(null);
  };
  const handleClear = () => {
    if (editing.event) clearEvent(editing.event.id);
    setEditing(null);
  };

  return (
    <div style={{ background: 'var(--dl-canvas)', minHeight: '100%', padding: '24px 20px', font: 'var(--dl-type-body)', color: 'var(--dl-text)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <h1 style={{ font: 'var(--dl-weight-semibold) 28px/1.16 var(--dl-font-sans)', letterSpacing: '-0.01em', margin: 0 }}>
          Week of {formatWeekOf(weekOf)}
        </h1>
        {hasPlan && <StatusChip status={status || 'draft'} />}
        {hasPlan && (
          <span style={{ whiteSpace: 'nowrap', font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-secondary)' }}>
            {plannedCount} of 7 planned
          </span>
        )}
        <span style={{ flex: 1, minWidth: 8 }} />
        <Button variant="secondary" icon="sparkles" onClick={onDraft} disabled={busy}>
          {hasPlan ? 'Redraft' : 'Draft this week'}
        </Button>
        {hasPlan && !locked && <Button variant="primary" icon="lock" onClick={lockWeek} disabled={busy}>Lock the week</Button>}
        {locked && <Button variant="secondary" icon="notebook-pen" onClick={revertWeek} disabled={busy}>Unlock to edit</Button>}
      </div>

      {notice && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', padding: '10px 14px', borderRadius: 12, background: 'var(--dl-primary-tint)', border: '1px solid var(--dl-primary-tint-border)', color: 'var(--dl-primary-text)', font: 'var(--dl-type-body-sm)' }}>
          <Icon name="lock" size={15} strokeWidth={2.25} />
          <span>{notice}</span>
          <span style={{ flex: 1, minWidth: 8 }} />
          <button
            type="button"
            onClick={revertWeek}
            disabled={busy}
            style={{ background: 'none', border: 'none', padding: 0, cursor: busy ? 'default' : 'pointer', color: 'var(--dl-primary-text)', font: 'inherit', fontWeight: 'var(--dl-weight-semibold)', textDecoration: 'underline' }}
          >
            Unlock now
          </button>
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 16, font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-secondary)' }}>
          Couldn&apos;t load the board. If this just launched, the plan tables may not be migrated yet.
        </div>
      )}

      {loading && <div style={{ font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-muted)' }}>Loading your week…</div>}

      {!loading && !hasPlan && !error && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 12, maxWidth: 420, padding: 24,
          background: 'var(--dl-surface)', border: '1px solid var(--dl-border)', borderRadius: 'var(--dl-radius-lg, 12px)',
        }}>
          <span style={{ font: 'var(--dl-weight-semibold) var(--dl-size-heading)/1.25 var(--dl-font-sans)' }}>Start this week&apos;s plan</span>
          <span style={{ font: 'var(--dl-type-body-sm)', color: 'var(--dl-text-secondary)' }}>
            Draft a realistic week from your household&apos;s tastes. You approve every night, then lock it to generate the shopping list.
          </span>
          <Button variant="primary" icon="sparkles" onClick={onDraft} disabled={busy}>Draft this week</Button>
        </div>
      )}

      {!loading && hasPlan && (
        <div className="dl-week-grid">
          {cols.map((col) => {
              const variant = col.date === today ? 'today' : col.isBusy ? 'busy' : 'open';
              return (
                <div key={col.day} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                  <DayHeader day={col.day} date={col.date} variant={variant} badge={col.isBusy ? <Badge icon="clock">Busy</Badge> : null} />
                  {col.note && <span style={{ padding: '0 10px', font: 'var(--dl-type-meta)', color: 'var(--dl-text-muted)' }}>{col.note}</span>}
                  <div onClick={() => openEditor(col.date)} style={{ cursor: locked ? 'default' : 'pointer' }}>
                    {col.empty || !col.meal ? <EmptyNightSlot /> : <MealCard title={col.meal.title} status={col.meal.status} source={col.meal.source} />}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {locked && hasPlan && (
        <div style={{ marginTop: 32 }}>
          <div style={{ display: 'inline-flex', gap: 4, padding: 4, background: 'var(--dl-surface-sunken)', borderRadius: 'var(--dl-radius-md, 8px)', marginBottom: 12 }}>
            <TabButton active={lockedTab === 'shopping'} onClick={() => setLockedTab('shopping')}>Shopping list</TabButton>
            <TabButton active={lockedTab === 'prep'} onClick={() => setLockedTab('prep')}>Prep by day</TabButton>
          </div>
          {lockedTab === 'shopping'
            ? <ShoppingListPanel items={groceries} checked={checkedItems} onToggle={toggleItem} />
            : <PrepPanel days={prep} checked={prepChecked} onToggle={togglePrep} />}
        </div>
      )}

      {editing && (
        <NightEditor
          date={editing.date}
          event={editing.event}
          recipes={recipes}
          busy={busy}
          onSave={handleSave}
          onClear={handleClear}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
